<template>
  <card-form-content>
    <template v-slot:header>
      <div class="flex items-center justify-between gap-3">
        <h2>{{ _mft('crm:leadDealCard.domainNamePlural') }}</h2>
        <span class="px-2 py-1 text-xs font-semibold text-gray-700 border rounded-full border-gray-200 bg-gray-50">
          {{ _mft('crm:leadDealCard.trackedCount', { count: filterCounts.all }) }}
        </span>
      </div>
    </template>

    <template v-slot:form>
      <div>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h5>{{ _mft('crm:leadDealCard.momentumBuilder') }}</h5>
            <p class="text-xs text-gray-600">{{ _mft('crm:leadDealCard.momentumBuilderDescription') }}</p>
          </div>
        </div>

        <div class="mt-1">
          <input-select
            v-model:content="creatingDeal.type"
            :options="typeOptions"
            valueSpecification="id"
            labelSpecification="type_name"
            :label="_mft('crm:leadDealCard.type')"
            required
            placeholder=""
            name="type"
          />

          <input-select
            v-model:content="creatingDeal.status"
            :options="creatingStatusOptions"
            :label="_mft('crm:leadDealCard.status')"
            required
            placeholder=""
            name="status"
          />

          <input-number
            :label="_mft('crm:leadDealCard.estimatedValue')"
            :content="parseInt(creatingDeal.value, 10) ? parseInt(creatingDeal.value, 10) : null"
            name="value"
            required
            v-model:content="creatingDeal.value"
          />
        </div>

        <div
          v-if="creatingSelectedStatusMeta"
          class="mt-3 text-xs text-gray-600"
        >
          {{ _mft('crm:leadDealCard.statusContributesPrefix') }}
          <span class="font-semibold">{{ creatingSelectedStatusMeta.likelihood }}%</span>
          {{ _mft('crm:leadDealCard.statusContributesSuffix') }}
        </div>

        <div class="flex justify-end mt-4">
          <button-success
            :disabled="!creatingDealIsValid"
            :busy="working"
            v-on:click="createDeal"
          >
              {{ _mft('crm:leadDealCard.addDeal') }}
          </button-success>
        </div>
      </div>
    </template>

    <template v-slot:content>
      <div class="space-y-4">
        <div class="grid gap-3 md:grid-cols-4">
          <div class="p-3 rounded-xl border border-yellow-200 bg-gradient-to-br from-yellow-50 to-yellow-100">
            <p class="text-xs uppercase tracking-wide text-yellow-700">{{ _mft('crm:leadDealCard.openDeals') }}</p>
            <p class="text-xl font-semibold text-yellow-900">{{ dealMetrics.openCount }}</p>
            <p class="text-xs text-yellow-800">{{ formatDealValue(dealMetrics.openValue) }}</p>
          </div>
          <div class="p-3 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
            <p class="text-xs uppercase tracking-wide text-blue-700">{{ _mft('crm:leadDealCard.weightedOpen') }}</p>
            <p class="text-xl font-semibold text-blue-900">{{ formatDealValue(dealMetrics.weightedOpenValue) }}</p>
            <p class="text-xs text-blue-800">{{ _mft('crm:leadDealCard.forecastPotential') }}</p>
          </div>
          <div class="p-3 rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100">
            <p class="text-xs uppercase tracking-wide text-green-700">{{ _mft('crm:leadDealCard.wonValue') }}</p>
            <p class="text-xl font-semibold text-green-900">{{ formatDealValue(dealMetrics.wonValue) }}</p>
            <p class="text-xs text-green-800">{{ _mft('crm:leadDealCard.closedWon') }}</p>
          </div>
          <div class="p-3 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100">
            <p class="text-xs uppercase tracking-wide text-gray-700">{{ _mft('crm:leadDealCard.totalValue') }}</p>
            <p class="text-xl font-semibold text-gray-900">{{ formatDealValue(dealMetrics.totalValue) }}</p>
            <p class="text-xs text-gray-700">{{ _mft('crm:leadDealCard.acrossDeals', { count: dealMetrics.totalCount }) }}</p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <button-base
            v-for="filter in dealFilters"
            :key="filter.key"
            type="button"
            :style-type="dealFilter === filter.key ? 'tinted' : 'secondary'"
            asda-class="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold transition border rounded-full"
            :asda-class="dealFilter === filter.key
              ? 'border-wlPrimary bg-wlPrimary text-wlPrimaryContrast shadow-sm'
              : 'border-gray-200 text-gray-600 bg-white hover:border-wlPrimary hover:text-wlPrimary'"
            @click="dealFilter = filter.key"
          >
              <template
                  #icon
                  v-if="filterCounts[filter.key] !== undefined && filterCounts[filter.key] > 0"
              >
                  <span class="font-bold">{{ filterCounts[filter.key] || 0 }}</span>
              </template>

            <span>{{ filter.label }}</span>
          </button-base>
        </div>

        <p
          v-if="visibleDeals.length === 0"
          class="p-4 text-sm text-center border border-dashed rounded-xl text-gray-500 border-gray-300 bg-white/70"
        >
          {{ dealFilter === 'all' ? _mft('crm:leadDealCard.noDealsAdded') : _mft('crm:leadDealCard.noDealsInSegmentYet') }}
        </p>

        <ul
          v-else
          role="list"
          class="grid gap-4 md:grid-cols-2"
        >
          <li
            v-for="deal in visibleDeals"
            :key="deal.id"
            class="col-span-1"
          >
            <article
              class="p-4 transition border shadow-sm rounded-2xl"
              :class="isDealEditable(deal)
                ? 'cursor-pointer border-gray-200 bg-white hover:border-myflowMagenta hover:shadow-md'
                : 'cursor-not-allowed border-gray-200 bg-gray-50'"
              @click="onDealCardClick(deal)"
            >
              <div class="flex items-start justify-between gap-2">
                <div>
                  <h3 class="text-sm font-semibold text-gray-900">{{ dealTypeName(deal) }}</h3>
                  <p class="text-xs text-gray-500 mt-0.5">{{ statusMetaForDeal(deal).name }}</p>
                </div>
                <span
                  class="px-2 py-1 text-xs font-semibold border rounded-full"
                  :class="statusBadgeClass(statusMetaForDeal(deal))"
                >
                  {{ dealStateLabel(statusMetaForDeal(deal)) }}
                </span>
              </div>

              <div class="mt-4">
                <div class="flex items-center justify-between text-xs text-gray-600">
                  <span>{{ _mft('crm:leadDealCard.likelihood') }}</span>
                  <span class="font-semibold">{{ statusMetaForDeal(deal).likelihood }}%</span>
                </div>
                <div class="h-2 mt-1 overflow-hidden bg-gray-200 rounded-full">
                  <div
                    class="h-full rounded-full"
                    :class="likelihoodBarClass(statusMetaForDeal(deal))"
                    :style="{ width: `${statusMetaForDeal(deal).likelihood}%` }"
                  ></div>
                </div>
              </div>

              <div class="flex justify-between mt-4">
                <div>
                  <p class="text-xs mb-1 text-gray-500">{{ _mft('crm:leadDealCard.dealValue') }}</p>
                  <p class="text-sm font-semibold text-gray-900">{{ formatDealValue(deal.value) }}</p>
                </div>
                <div class="text-right">
                  <p class="text-xs mb-1 text-gray-500">{{ _mft('crm:leadDealCard.weighted') }}</p>
                  <p class="text-sm font-semibold text-gray-900">{{ formatDealValue(weightedValueForDeal(deal)) }}</p>
                </div>
              </div>

              <div class="flex items-center justify-between pt-3 mt-3 text-xs border-t border-gray-100">
                <span v-if="Number(isInternal) === 1 && deal.invoice != null" class="font-semibold text-gray-600">
                  {{ _mft('crm:leadDealCard.invoicedAt', { date: deal.invoice !== '2099-11-30' ? deal.invoice : _mft('crm:leadDealCard.processing') }) }}
                  <span v-if="deal.paid"> • {{ _mft('crm:leadDealCard.paidAt', { date: deal.paid }) }}</span>
                </span>
                <span v-else></span>

                <span
                  v-if="isDealEditable(deal)"
                  class="font-semibold text-myflowMagenta"
                >
                  {{ _mft('crm:leadDealCard.edit') }}
                </span>
                <span
                  v-else
                  class="font-semibold text-gray-400"
                >
                  {{ _mft('crm:leadDealCard.locked') }}
                </span>
              </div>
            </article>
          </li>
        </ul>
      </div>

      <deal-edit-slideout
        v-model:open="open"
        :deal="currentlyEditingDeal"
        @update:deal="onEditingDealChanged"
        :type-options="typeOptions"
        :status-options="editingStatusOptions"
        :busy="working"
        :disabled="!currentlyEditingDealIsValid"
        :delete-url="currentlyEditingDealDeleteUrl"
        @submit="updateDeal"
      />
    </template>
  </card-form-content>
</template>

<script>
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import DealEditSlideout from './dealEditSlideout.vue';
import { useNotificationStore } from '@/stores/NotificationStore.js';

const createEmptyCreatingDeal = () => ({
  type: null,
  status: null,
  value: null,
});

const createEmptyEditingDeal = () => ({
  id: '',
  type: '',
  status: '',
  value: '',
});

export default defineComponent({
  components: {
    DealEditSlideout,
  },
  props: {
    alignment: String,
    lead: Object,
    leadId: [Number, String],
    internal: Boolean,
  },
  setup(props, { slots }) {
    const notificationStore = useNotificationStore();

    const creatingDeal = ref(createEmptyCreatingDeal());
    const currentlyEditingDeal = ref(createEmptyEditingDeal());
    const localDeals = ref([]);
    const typeOptions = ref([]);
    const statusOptionsByType = ref({});
    const open = ref(false);
    const working = ref(false);
    const isInternal = ref(props.internal);
    const dealFilter = ref('all');
    const resolvedLeadId = computed(() => {
      if (props.leadId !== null && props.leadId !== undefined) {
        return Number(props.leadId);
      }

      if (props.lead?.id !== null && props.lead?.id !== undefined) {
        return Number(props.lead.id);
      }

      return null;
    });

    const toNumber = (value) => Number(value || 0);

    const normalizeDeal = (deal) => ({
      ...deal,
      type: deal?.type !== null && deal?.type !== undefined ? Number(deal.type) : deal?.type,
      invoice: deal?.invoice ?? deal?.invoice_at ?? null,
      paid: deal?.paid ?? deal?.paid_at ?? null,
    });

    const buildStatusOptions = (typeId, currentStatus = null) => {
      const statuses = statusOptionsByType.value[typeId] || [];
      const options = statuses.map((status) => ({
        value: status.name,
        label: `${status.name} (${status.likelihood}%)`,
      }));

      if (
        currentStatus &&
        !options.some((option) => option.value === currentStatus)
      ) {
        options.unshift({
          value: currentStatus,
          label: `${currentStatus} (${_mft('crm:leadDealCard.legacyStatus')})`,
        });
      }

      return options;
    };

    const findStatusForType = (typeId, statusName = null, statusId = null) => {
      const statuses = statusOptionsByType.value[Number(typeId)] || [];

      if (statusId !== null && statusId !== undefined) {
        const byId = statuses.find((status) => Number(status.id) === Number(statusId));
        if (byId) {
          return byId;
        }
      }

      if (!statusName) {
        return null;
      }

      const normalizedStatus = String(statusName).trim().toLowerCase();
      return statuses.find((status) => String(status.name).trim().toLowerCase() === normalizedStatus) || null;
    };

    const inferMetaFromStatusName = (statusName) => {
      const normalized = String(statusName || '').trim().toLowerCase();

      if (normalized.includes('won') || normalized.includes('vunnen') || normalized.includes('vunnet')) {
        return { likelihood: 100, is_closed: true, is_won: true };
      }

      if (
        normalized.includes('lost') ||
        normalized.includes('förlorad') ||
        normalized.includes('forlorad') ||
        normalized.includes('tapt')
      ) {
        return { likelihood: 0, is_closed: true, is_won: false };
      }

      if (normalized.includes('offer') || normalized.includes('offert') || normalized.includes('tilbud')) {
        return { likelihood: 60, is_closed: false, is_won: false };
      }

      if (normalized.includes('meeting') || normalized.includes('möte') || normalized.includes('møte')) {
        return { likelihood: 30, is_closed: false, is_won: false };
      }

      if (normalized.includes('hold') || normalized.includes('pausad') || normalized.includes('avvaktande')) {
        return { likelihood: 20, is_closed: false, is_won: false };
      }

      if (normalized.includes('contact') || normalized.includes('kontakt')) {
        return { likelihood: 10, is_closed: false, is_won: false };
      }

      return { likelihood: 50, is_closed: false, is_won: false };
    };

    const statusMetaForDeal = (deal) => {
      const typeId = Number(deal.type || 0);
      const status = findStatusForType(typeId, deal.status, deal.status_id);

      if (status) {
        return {
          name: status.name,
          likelihood: Number(status.likelihood || 0),
          is_closed: !!status.is_closed,
          is_won: !!status.is_won,
        };
      }

      const fallbackName = String(deal.status || '').trim();
      const inferred = inferMetaFromStatusName(fallbackName);

      return {
        name: fallbackName || _mft('crm:leadDealCard.unknownStatus'),
        ...inferred,
      };
    };

    const weightedValueForDeal = (deal) => {
      const value = toNumber(deal.value);
      const likelihood = Number(statusMetaForDeal(deal).likelihood || 0);

      return Math.round(value * (likelihood / 100));
    };

    const sortDeals = (deals) => {
      return [...deals].sort((a, b) => {
        const aMeta = statusMetaForDeal(a);
        const bMeta = statusMetaForDeal(b);

        if (aMeta.is_closed !== bMeta.is_closed) {
          return aMeta.is_closed ? 1 : -1;
        }

        const weightedDelta = weightedValueForDeal(b) - weightedValueForDeal(a);
        if (weightedDelta !== 0) {
          return weightedDelta;
        }

        return toNumber(b.value) - toNumber(a.value);
      });
    };

    const normalizeDeals = (deals) => {
      const normalized = Array.isArray(deals)
        ? deals
        : Object.values(deals || {});

      return sortDeals(normalized.map(normalizeDeal));
    };

    const fetchDeals = async () => {
      if (!resolvedLeadId.value) {
        localDeals.value = [];
        return;
      }

      try {
        const response = await axios.get(route('bo.api.v1.crm.lead.deals', resolvedLeadId.value));
        const payload = response.data?.deals?.data ?? response.data?.deals ?? [];

        localDeals.value = normalizeDeals(payload);
      } catch (error) {
        localDeals.value = [];
        console.error(error);
      }
    };

    const ensureStatusValueForType = (dealRef) => {
      const currentDeal = dealRef.value;
      if (!currentDeal || !currentDeal.type) {
        return;
      }

      const options = buildStatusOptions(Number(currentDeal.type), currentDeal.status);
      if (options.length === 0) {
        return;
      }

      if (!currentDeal.status || !options.some((option) => option.value === currentDeal.status)) {
        currentDeal.status = options[0].value;
      }
    };

    const resetCreatedealForm = () => {
      creatingDeal.value = createEmptyCreatingDeal();
    };

    const closeSlideout = () => {
      open.value = false;
    };

    const openInSlideout = (deal) => {
      currentlyEditingDeal.value = {
        id: deal.id,
        type: deal.type ? Number(deal.type) : null,
        status: deal.status ?? null,
        value: deal.value,
      };
      ensureStatusValueForType(currentlyEditingDeal);
      open.value = true;
    };

    const isDealEditable = (deal) => {
      return !(Number(isInternal.value) === true && deal.invoice != null);
    };

    const onDealCardClick = (deal) => {
      if (!isDealEditable(deal)) {
        return;
      }

      openInSlideout(deal);
    };

    const creatingDealIsValid = computed(() => {
      if (isNaN(creatingDeal.value.value)) {
        return false;
      }

      if (!creatingDeal.value.type || !creatingDeal.value.status || creatingDeal.value.value === null) {
        return false;
      }

      return true;
    });

    const currentlyEditingDealIsValid = computed(() => {
      if (isNaN(currentlyEditingDeal.value.value)) {
        return false;
      }

      if (
        !currentlyEditingDeal.value.type ||
        !currentlyEditingDeal.value.status ||
        currentlyEditingDeal.value.value === null
      ) {
        return false;
      }

      return true;
    });

    const createDeal = async () => {
      if (!creatingDealIsValid.value || !resolvedLeadId.value) {
        return;
      }

      working.value = true;
      const payload = {
        ...creatingDeal.value,
        type: creatingDeal.value.type ? Number(creatingDeal.value.type) : null,
      };

      try {
        await axios.post(route('crm.leads.deals.add', resolvedLeadId.value), payload);
        await fetchDeals();
        resetCreatedealForm();
        notificationStore.addNotification({
          type: 'success',
          title: _mft('crm:deal.created'),
          dismissAfter: 2500,
        });
      } finally {
        working.value = false;
      }
    };

    const updateDeal = async () => {
      working.value = true;

      const payload = {
        ...currentlyEditingDeal.value,
        type: currentlyEditingDeal.value.type ? Number(currentlyEditingDeal.value.type) : null,
      };

      try {
        await axios.post(route('crm.deal.update', currentlyEditingDeal.value.id), payload);
        await fetchDeals();

        notificationStore.addNotification({
          type: 'success',
          title: _mft('crm:deal.updated'),
          dismissAfter: 2500,
        });

        closeSlideout();
      } finally {
        working.value = false;
      }
    };

    const onEditingDealChanged = (updatedDeal) => {
      currentlyEditingDeal.value = updatedDeal;
    };

    const dealTypeName = (deal) => {
      if (deal?.lead_deal_type_rel?.type_name) {
        return deal.lead_deal_type_rel.type_name;
      }

      const type = typeOptions.value.find((option) => Number(option.id) === Number(deal.type));
      return type?.type_name || _mft('crm:leadDealCard.dealFallback');
    };

    const dealStateLabel = (meta) => {
      if (meta.is_won) {
        return _mft('crm:leadDealCard.stateWon');
      }

      if (meta.is_closed) {
        return _mft('crm:leadDealCard.stateClosed');
      }

      return _mft('crm:leadDealCard.stateOpen');
    };

    const statusBadgeClass = (meta) => {
      if (meta.is_won) {
        return 'text-green-700 border-green-200 bg-green-100';
      }

      if (meta.is_closed) {
        return 'text-rose-700 border-rose-200 bg-rose-100';
      }

      return 'text-yellow-700 border-yellow-200 bg-yellow-100';
    };

    const likelihoodBarClass = (meta) => {
      if (meta.is_won) {
        return 'bg-green-500';
      }

      if (meta.is_closed) {
        return 'bg-rose-500';
      }

      return 'bg-gradient-to-r from-indigo-500 to-cyan-500';
    };

    const formatDealValue = (value) => `${toNumber(value).toLocaleString()} `+window.myflow.company.currency;

    const getDealTypes = async () => {
      const response = await axios.get(route('crm.deal.types'));
      typeOptions.value = response.data;

      const statusMap = {};
      typeOptions.value.forEach((type) => {
        statusMap[type.id] = type.statuses || [];
      });

      statusOptionsByType.value = statusMap;
      ensureStatusValueForType(creatingDeal);
      ensureStatusValueForType(currentlyEditingDeal);
      localDeals.value = sortDeals(localDeals.value);
    };

    watch(() => creatingDeal.value.type, () => {
      ensureStatusValueForType(creatingDeal);
    });

    watch(() => currentlyEditingDeal.value.type, () => {
      ensureStatusValueForType(currentlyEditingDeal);
    });

    watch(resolvedLeadId, () => {
      fetchDeals();
    }, { immediate: true });

    onMounted(() => {
      getDealTypes();
    });

    const hasHeaderSlot = computed(() => !!slots['header-left'] && !!slots['header-right']);
    const hasImageSlot = computed(() => !!slots.image);
    const hasBodySlot = computed(() => !!slots.body);
    const alignmentClass = computed(() => (props.alignment === 'center' ? 'text-center' : ''));

    const currentlyEditingDealDeleteUrl = computed(() => {
      if (!currentlyEditingDeal.value.id) {
        return '';
      }

      return route('crm.deal.delete', currentlyEditingDeal.value.id);
    });

    const creatingStatusOptions = computed(() => {
      if (!creatingDeal.value.type) {
        return [];
      }

      return buildStatusOptions(Number(creatingDeal.value.type), creatingDeal.value.status);
    });

    const editingStatusOptions = computed(() => {
      if (!currentlyEditingDeal.value.type) {
        return [];
      }

      return buildStatusOptions(Number(currentlyEditingDeal.value.type), currentlyEditingDeal.value.status);
    });

    const creatingSelectedStatusMeta = computed(() => {
      if (!creatingDeal.value.type || !creatingDeal.value.status) {
        return null;
      }

      const status = findStatusForType(creatingDeal.value.type, creatingDeal.value.status);
      if (status) {
        return {
          likelihood: Number(status.likelihood || 0),
          is_closed: !!status.is_closed,
          is_won: !!status.is_won,
        };
      }

      return inferMetaFromStatusName(creatingDeal.value.status);
    });

    const dealFilters = computed(() => {
      return [
        { key: 'all', label: _mft('crm:leadDealCard.filter.all') },
        { key: 'open', label: _mft('crm:leadDealCard.filter.open') },
        { key: 'won', label: _mft('crm:leadDealCard.filter.won') },
        { key: 'closed', label: _mft('crm:leadDealCard.filter.closedLost') },
      ];
    });

    const filterCounts = computed(() => {
      const counts = {
        all: localDeals.value.length,
        open: 0,
        won: 0,
        closed: 0,
      };

      localDeals.value.forEach((deal) => {
        const meta = statusMetaForDeal(deal);
        if (meta.is_won) {
          counts.won += 1;
          return;
        }

        if (meta.is_closed) {
          counts.closed += 1;
          return;
        }

        counts.open += 1;
      });

      return counts;
    });

    const dealMetrics = computed(() => {
      let totalValue = 0;
      let openValue = 0;
      let weightedOpenValue = 0;
      let wonValue = 0;
      let openCount = 0;

      localDeals.value.forEach((deal) => {
        const value = toNumber(deal.value);
        const meta = statusMetaForDeal(deal);

        totalValue += value;

        if (meta.is_closed) {
          if (meta.is_won) {
            wonValue += value;
          }

          return;
        }

        openCount += 1;
        openValue += value;
        weightedOpenValue += weightedValueForDeal(deal);
      });

      return {
        totalCount: localDeals.value.length,
        openCount,
        openValue,
        weightedOpenValue,
        wonValue,
        totalValue,
      };
    });

    const visibleDeals = computed(() => {
      if (dealFilter.value === 'all') {
        return sortDeals(localDeals.value);
      }

      const filtered = localDeals.value.filter((deal) => {
        const meta = statusMetaForDeal(deal);

        if (dealFilter.value === 'open') {
          return !meta.is_closed;
        }

        if (dealFilter.value === 'won') {
          return meta.is_won;
        }

        if (dealFilter.value === 'closed') {
          return meta.is_closed && !meta.is_won;
        }

        return true;
      });

      return sortDeals(filtered);
    });

    return {
      alignmentClass,
      createDeal,
      creatingDeal,
      creatingDealIsValid,
      creatingSelectedStatusMeta,
      creatingStatusOptions,
      currentlyEditingDeal,
      currentlyEditingDealDeleteUrl,
      currentlyEditingDealIsValid,
      dealFilter,
      dealFilters,
      dealMetrics,
      dealStateLabel,
      dealTypeName,
      editingStatusOptions,
      filterCounts,
      formatDealValue,
      hasBodySlot,
      hasHeaderSlot,
      hasImageSlot,
      isDealEditable,
      isInternal,
      likelihoodBarClass,
      localDeals,
      onDealCardClick,
      onEditingDealChanged,
      open,
      statusBadgeClass,
      statusMetaForDeal,
      typeOptions,
      updateDeal,
      visibleDeals,
      weightedValueForDeal,
      working,
    };
  },
});
</script>
